import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import clsx from 'clsx';
import { useRouter } from 'next/dist/client/router';
import { SearchStatus, PageLinks } from '../../../constants';
import useScrollToElement from '../../../hooks/useScrollToElement';
import getCabinGroupsDictionary from '../../../helpers/getCabinGroupsDictionary';
import CruiseLineAdvisories from '../../CruiseLineAdvisories';
import withSpinner from '../../../HOCs/withSpinner';
import Ship from '../../../assets/svgs/ship.svg';
import FilterItem from './FilterItem';
import CabinGroup from './CabinGroup';
import { ratesRequest, ratesSuccess, setStateroomTab } from '../../../store/slices/cruise';
import RateOptionPriceDisclaimerTooltip from '../RateOptionPriceDisclaimerTooltip';
import getCabinCategoryMinRate from '../../../helpers/getCabinCategoryMinRate';
import formatCruiseSearchParams from '../../../helpers/formatters/formatCruiseSearchParams';
import formatStateroomsForRequest from '../../../helpers/formatters/formatStateroomsForRequest';

const Rates = ({
  className,
  onModalClose,
  tripId,
  itineraryId,
  cruiseItinerary,
  selectedItineraryRow,
  doShowPrice,
  searchResultsTravelOption,
  tripRoomSearchInput,
}) => {
  const travelOption = (selectedItineraryRow || {}).travelOption || searchResultsTravelOption;
  const itinerary =
    selectedItineraryRow ||
    (travelOption &&
      travelOption.itineraryRows &&
      _.find(travelOption.itineraryRows, (it) => it.itineraryId === itineraryId));
  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const { isViewingCompare } = useSelector((state) => state.compare);
  const [selectedCabin, setSelectedCabin] = useState('All');
  const [cabinGroups, setCabinGroups] = useState(null);
  const [cabinTypes, setCabinTypes] = useState(null);
  const dispatch = useDispatch();
  const router = useRouter();
  const { pageName } = router.query;
  const {
    cabinCategories,
    ratesStatus,
    cabinCategoriesCruiseLineAdvisories,
    rateErrors,
    stateroomTab,
    itineraryDetailError,
    itineraryDetailStatus,
  } = useSelector((state) => state.cruise);
  const staterooms = useSelector((state) => state.tripSearch.searchObj.staterooms);
  const doHaveItineraryDetail = !itineraryDetailError;

  const memoizedScrollCondition = useMemo(
    () => ({
      value: stateroomTab.doScroll && ratesStatus === SearchStatus.DONE,
    }),
    [stateroomTab, ratesStatus],
  );

  const tabsEl = useScrollToElement(memoizedScrollCondition);

  const { tripConfirmation } = useSelector((state) => state.trip);
  const isConfirmation =
    pageName !== PageLinks.ChangeCruise.pageName &&
    pageName !== PageLinks.AddCruise.pageName &&
    tripConfirmation != null &&
    tripConfirmation.bookingResponses != null &&
    tripConfirmation.bookingResponses[0] != null;
  const { track } = useAnalytics();

  useEffect(() => {
    if (cruiseItinerary) {
      if (
        cruiseItinerary.cabins &&
        cruiseItinerary.cabins[stateroomTab.tabNumber].selectedShipCabinCategory
      ) {
        dispatch(
          ratesSuccess({
            cabinCategories: [
              cruiseItinerary.cabins[stateroomTab.tabNumber].selectedShipCabinCategory,
            ],
          }),
        );
      }
      if (
        cruiseItinerary.cabinCategories &&
        cruiseItinerary.cabinCategories[stateroomTab.tabNumber] &&
        cruiseItinerary.cabinCategories[stateroomTab.tabNumber].cabinCategories
      ) {
        dispatch(
          ratesSuccess({
            cabinCategories:
              cruiseItinerary.cabinCategories[stateroomTab.tabNumber].cabinCategories,
          }),
        );
      }
    } else {
      const cruiseSearchRQ = formatCruiseSearchParams(itinerary, travelOption);
      dispatch(
        ratesRequest({
          itineraryId,
          ...formatStateroomsForRequest(
            staterooms[stateroomTab.tabNumber],
            cruiseSearchRQ.cruiseLineId,
          ),
          ...cruiseSearchRQ,
        }),
      );
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [cruiseItinerary, dispatch, staterooms, itineraryId, stateroomTab]);

  const handleFilterCabinClick = useCallback(
    (cabinType) => {
      setSelectedCabin(cabinType);
      track('rates.handleFilterCabinClick', {
        cabinType,
      });
    },
    [setSelectedCabin, track],
  );

  useEffect(() => {
    if (cabinCategories) {
      setCabinGroups(getCabinGroupsDictionary(cabinCategories));
      setCabinTypes(Object.keys(_.groupBy(cabinCategories, 'cabinCategoryLocation')));
    }
  }, [cabinCategories]);

  const filteredCabinGroups = useMemo(
    () =>
      (cabinTypes || []).filter(
        (type) => !cabinTypes.includes(selectedCabin) || type === selectedCabin,
      ),
    [cabinTypes, selectedCabin],
  );

  const handleClickTab = (index) => {
    dispatch(
      setStateroomTab({
        tabNumber: index,
        doScroll: false,
      }),
    );
  };

  const filterItemValueElement = (cabins) => {
    const { rate, isTotalAmount } = getCabinCategoryMinRate({ cabins, cabinGroups });

    return (
      <RateOptionPriceDisclaimerTooltip
        rate={rate || 0}
        decimalScale={0}
        isTotalAmount={isTotalAmount || false}
      />
    );
  };

  const renderRates = () => (
    <>
      {doHaveItineraryDetail && cabinCategories && cabinGroups && cabinCategories.length > 1 && (
        <>
          {!isViewingBrochure && !isViewingCompare && (
            <div className="m-5">
              Rates below are total amounts, include non-commissionable fares, and do not include
              taxes and fees.
            </div>
          )}
          <div className="sm:grid grid-flow-col mt-5">
            <div className="font-bold text-lg my-5 mx-5">
              {isConfirmation ? 'Selected ' : 'Select '}
              cabin and rate
            </div>
            {/* TODO: fix cruise sections menu for compare, the menu should scrollable
              horizontally on mobile to see the whole thing.
              Problem is related parent table element */}
            <div
              className={clsx(
                'grid-flow-col sm:auto-cols-auto auto-cols-max mb-5 text-xs sm:text-sm overflow-x-auto ml-2 sm:ml-0',
                { grid: !isViewingCompare, 'sm:grid': isViewingCompare },
              )}
            >
              {['All', ...cabinTypes].map(
                (cabinType) =>
                  (cabinType === 'All' || cabinGroups[cabinType]) && (
                    <FilterItem
                      key={cabinType}
                      onSelectFilter={handleFilterCabinClick}
                      cabinType={cabinType}
                      isSelected={selectedCabin === cabinType}
                      value={doShowPrice ? filterItemValueElement(cabinGroups[cabinType]) : null}
                    />
                  ),
              )}
            </div>
          </div>
        </>
      )}
      <CruiseLineAdvisories
        advisories={cabinCategoriesCruiseLineAdvisories || []}
        className="mx-5 mb-5 p-5"
      />
      {doHaveItineraryDetail &&
        cabinGroups &&
        filteredCabinGroups.map(
          (location) =>
            cabinGroups[location] && (
              <CabinGroup
                key={location}
                title={location}
                cabinGroup={cabinGroups[location]}
                onModalClose={onModalClose}
                tripId={tripId}
                isConfirmation={isConfirmation}
                selectedRoomIndex={stateroomTab.tabNumber}
                doShowPrice={doShowPrice}
                travelOption={travelOption}
                tripRoomSearchInput={tripRoomSearchInput}
              />
            ),
        )}
      <>
        {(!cabinCategories || !cabinCategories.length || !doHaveItineraryDetail) && (
          <div className="text-gray-600 font-bold my-10 mx-5 flex flex-col items-center">
            <Ship />
            No rates found
            {rateErrors && rateErrors.length && (
              <>
                <hr />
                <ul className="list-disc list-outside w-11/12 m-auto flex flex-col items-center">
                  {rateErrors.map((item) => (
                    <li key={item.code}>{item.description}</li>
                  ))}
                </ul>
              </>
            )}
            {!doHaveItineraryDetail ? (
              <>
                <hr />
                <div>{itineraryDetailError}</div>
              </>
            ) : (
              <></>
            )}
          </div>
        )}
      </>
    </>
  );

  return (
    <div ref={tabsEl} className={className}>
      {doHaveItineraryDetail && staterooms && staterooms.length > 1 && (
        <div className="text-sm font-bold text-theme flex pt-5 md:pl-2 mb-2 pl-0 border-b">
          {staterooms.map((stateroom, index) => (
            <div
              key={`${index.toString()}`}
              className={clsx(
                {
                  'border bg-white rounded-md rounded-b-none border-b-0':
                    index === stateroomTab.tabNumber,
                },
                'px-5 py-3 text-center',
              )}
              style={index === stateroomTab.tabNumber ? { marginBottom: -1 } : {}}
            >
              <button
                type="button"
                className="focus:outline-none h-full border-0 font-bold"
                onClick={() => handleClickTab(index)}
                // onClick={() => setSelectedTab(index)}
              >
                Stateroom {index + 1}
              </button>
            </div>
          ))}
        </div>
      )}

      {withSpinner(
        renderRates,
        (ratesStatus !== SearchStatus.DONE || itineraryDetailStatus !== SearchStatus.DONE) &&
          doHaveItineraryDetail,
      )}
    </div>
  );
};

Rates.propTypes = {
  className: PropTypes.string,
  onModalClose: PropTypes.func,
  tripId: PropTypes.string,
  itineraryId: PropTypes.string,
  cruiseItinerary: PropTypes.objectOf(PropTypes.objectOf),
  selectedItineraryRow: PropTypes.objectOf(PropTypes.objectOf),
  doShowPrice: PropTypes.bool,
  searchResultsTravelOption: PropTypes.shape({
    mode: PropTypes.number,
    key: PropTypes.string,
  }),
  tripRoomSearchInput: PropTypes.arrayOf(PropTypes.shape()),
};

Rates.defaultProps = {
  className: undefined,
  onModalClose: undefined,
  tripId: undefined,
  itineraryId: undefined,
  cruiseItinerary: undefined,
  selectedItineraryRow: undefined,
  doShowPrice: true,
  searchResultsTravelOption: undefined,
  tripRoomSearchInput: undefined,
};

export default memo(Rates);
